import React, { useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

const Documents = () => {
  const toast = useRef(null);
  const [generalDocuments] = useState([
    {
      id: 1,
      title: 'Endurance Riding Rules 2024',
      type: 'Rules',
      category: 'General',
      uploadDate: new Date('2024-01-15'),
      fileSize: '2.5 MB',
      description: 'Official rules and regulations for endurance riding competitions.'
    },
    {
      id: 2,
      title: 'Safety Guidelines',
      type: 'Guidelines',
      category: 'General',
      uploadDate: new Date('2024-02-01'),
      fileSize: '1.8 MB',
      description: 'Essential safety information for all participants.'
    },
    {
      id: 3,
      title: 'Veterinary Requirements',
      type: 'Requirements',
      category: 'General',
      uploadDate: new Date('2024-02-15'),
      fileSize: '1.2 MB',
      description: 'Veterinary check requirements and procedures.'
    }
  ]);

  const [memberDocuments] = useState([
    {
      id: 4,
      title: 'Member Handbook 2024',
      type: 'Handbook',
      category: 'Member',
      uploadDate: new Date('2024-01-10'),
      fileSize: '3.2 MB',
      description: 'Complete guide for Sport Endurance members.'
    },
    {
      id: 5,
      title: 'Training Guide for Beginners',
      type: 'Training',
      category: 'Member',
      uploadDate: new Date('2024-03-01'),
      fileSize: '4.1 MB',
      description: 'Step-by-step training guide for new endurance riders.'
    },
    {
      id: 6,
      title: 'Annual Report 2023',
      type: 'Report',
      category: 'Member',
      uploadDate: new Date('2024-01-31'),
      fileSize: '2.8 MB',
      description: 'Overview of achievements and activities in 2023.'
    }
  ]);

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [displayPreview, setDisplayPreview] = useState(false);

  const categoryBodyTemplate = (rowData) => {
    const severity = rowData.category === 'General' ? 'info' : 'success';
    return <Badge value={rowData.category} severity={severity} />;
  };

  const dateBodyTemplate = (rowData) => {
    return rowData.uploadDate.toLocaleDateString('en-GB');
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          tooltip="View Document"
          onClick={() => {
            setSelectedDocument(rowData);
            setDisplayPreview(true);
          }}
        />
        <Button
          icon="pi pi-download"
          className="p-button-text p-button-sm"
          tooltip="Download Document"
          onClick={() => {
            toast.current?.show({
              severity: 'info',
              summary: 'Download',
              detail: `Downloading: ${rowData.title}`,
              life: 3000
            });
          }}
        />
      </div>
    );
  };

  return (
    <div>
      <Toast ref={toast} />
      {/* General Documents */}
      <div className="se-card mb-4">
        <div className="se-card-header">
          <h2>Rules & General Documents</h2>
        </div>
        <div className="se-card-content">
          <p className="text-600 mb-3">
            Official rules, guidelines, and general information available to all members.
          </p>
          <DataTable value={generalDocuments} responsiveLayout="scroll">
            <Column field="title" header="Document Title" sortable />
            <Column field="type" header="Type" sortable />
            <Column field="category" header="Category" body={categoryBodyTemplate} />
            <Column field="uploadDate" header="Upload Date" body={dateBodyTemplate} sortable />
            <Column field="fileSize" header="File Size" />
            <Column header="Actions" body={actionBodyTemplate} />
          </DataTable>
        </div>
      </div>

      {/* Member Documents */}
      <div className="se-card">
        <div className="se-card-header">
          <h2>Member Documents</h2>
        </div>
        <div className="se-card-content">
          <p className="text-600 mb-3">
            Exclusive documents and resources available to Sport Endurance members.
          </p>
          <DataTable value={memberDocuments} responsiveLayout="scroll">
            <Column field="title" header="Document Title" sortable />
            <Column field="type" header="Type" sortable />
            <Column field="category" header="Category" body={categoryBodyTemplate} />
            <Column field="uploadDate" header="Upload Date" body={dateBodyTemplate} sortable />
            <Column field="fileSize" header="File Size" />
            <Column header="Actions" body={actionBodyTemplate} />
          </DataTable>
        </div>
      </div>

      {/* Document Preview Dialog */}
      <Dialog
        header={selectedDocument?.title}
        visible={displayPreview}
        style={{ width: '70vw', height: '80vh' }}
        onHide={() => setDisplayPreview(false)}
        footer={
          <div>
            <Button 
              label="Download" 
              icon="pi pi-download" 
              className="p-button-primary mr-2"
              onClick={() => {
                toast.current?.show({
                  severity: 'info',
                  summary: 'Download',
                  detail: `Downloading: ${selectedDocument?.title}`,
                  life: 3000
                });
              }}
            />
            <Button 
              label="Close" 
              icon="pi pi-times" 
              className="p-button-secondary"
              onClick={() => setDisplayPreview(false)}
            />
          </div>
        }
      >
        {selectedDocument && (
          <div>
            <div className="mb-3">
              <strong>Type:</strong> {selectedDocument.type}<br />
              <strong>Category:</strong> {selectedDocument.category}<br />
              <strong>Upload Date:</strong> {selectedDocument.uploadDate.toLocaleDateString('en-GB')}<br />
              <strong>File Size:</strong> {selectedDocument.fileSize}
            </div>
            <div className="mb-3">
              <strong>Description:</strong><br />
              {selectedDocument.description}
            </div>
            
            {/* Mock Document Preview */}
            <div className="border-1 border-200 border-round p-4 bg-gray-50" style={{ height: '400px' }}>
              <div className="text-center text-600">
                <i className="pi pi-file-pdf text-6xl mb-3 block"></i>
                <h4>Document Preview</h4>
                <p>This is a preview of: <strong>{selectedDocument.title}</strong></p>
                <p className="text-sm">In a real application, this would show the actual document content or PDF viewer.</p>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* GDPR Notice */}
      <div className="mt-4">
        <div className="se-card">
          <div className="se-card-content">
            <div className="flex align-items-start">
              <i className="pi pi-lock se-text-primary mr-3 mt-1"></i>
              <div>
                <h4 className="mt-0 mb-2">Privacy & Data Protection</h4>
                <p className="text-600 m-0">
                  All documents are stored securely and access is logged for security purposes. 
                  Member documents are only accessible to verified members in accordance with our privacy policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;